import React from 'react';
import { AdminIndustryOverrideRow } from '../../types';
import { getOpportunitySourceAriaLabel, getOpportunitySourceName } from '../../utils/opportunitySources';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface Props {
  rows: AdminIndustryOverrideRow[];
  taxonomy: string[];
  sortField: string;
  sortDirection: 'asc' | 'desc';
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  onSort: (field: string, defaultDirection?: 'asc' | 'desc') => void;
  onPageChange: (page: number) => void;
  onOverrideChange: (contractId: string, newValue: string | null) => void;
  onNoteChange: (contractId: string, newValue: string | null) => void;
}

const HEADER_COLUMNS: {
  key: string;
  label: string;
  sortable?: boolean;
  sortKey?: string;
  defaultDirection?: 'asc' | 'desc';
  width?: string;
}[] = [
  { key: 'title', label: 'Contract', sortable: true, sortKey: 'title', width: '320px' },
  { key: 'city', label: 'Procuring Organization', sortable: true, sortKey: 'city', width: '200px' },
  { key: 'status', label: 'Status', sortable: true, sortKey: 'status', width: '140px' },
  { key: 'firstSeen', label: 'Date Added', sortable: true, sortKey: 'first_seen', defaultDirection: 'desc', width: '200px' },
  { key: 'dueDate', label: 'Due Date', sortable: true, sortKey: 'due_date', width: '140px' },
  { key: 'industryDisplay', label: 'Industry', sortable: true, sortKey: 'industry_display', width: '160px' },
  { key: 'industryOverride', label: 'Override', sortable: false, width: '220px' },
  { key: 'overrideNote', label: 'Note', sortable: false, width: '260px' },
  { key: 'overrideAt', label: 'Modified', sortable: true, sortKey: 'override_at', defaultDirection: 'desc', width: '180px' },
  { key: 'overrideBy', label: 'By', sortable: true, sortKey: 'override_by', width: '140px' },
];

const IndustryOverrideTable: React.FC<Props> = ({
  rows,
  taxonomy,
  sortField,
  sortDirection,
  loading,
  page,
  pageSize,
  total,
  onSort,
  onPageChange,
  onOverrideChange,
  onNoteChange,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const showDropdown = totalPages <= 200;
  const [pageInput, setPageInput] = React.useState(page.toString());

  const columnWidthMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    HEADER_COLUMNS.forEach((column) => {
      if (column.width) {
        map[column.key] = column.width;
      }
    });
    return map;
  }, []);

  React.useEffect(() => {
    setPageInput(page.toString());
  }, [page]);

  const pageOptions = React.useMemo(() => {
    if (!showDropdown) return [];
    return Array.from({ length: totalPages }, (_, idx) => idx + 1);
  }, [showDropdown, totalPages]);

  const clampPage = (value: number) => {
    if (Number.isNaN(value)) return page;
    return Math.max(1, Math.min(totalPages, value));
  };

  const handleManualPageChange = () => {
    const parsed = clampPage(parseInt(pageInput, 10));
    setPageInput(parsed.toString());
    if (parsed !== page) {
      onPageChange(parsed);
    }
  };

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-[1100px] table-fixed divide-y divide-slate-100">
          <colgroup>
            {HEADER_COLUMNS.map((column) => (
              <col key={column.key} style={column.width ? { width: column.width } : undefined} />
            ))}
          </colgroup>
          <thead className="bg-slate-50">
            <tr>
              {HEADER_COLUMNS.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(column.sortKey ?? column.key, column.defaultDirection)}
                      className="inline-flex items-center gap-1 text-slate-700 hover:text-blue-600"
                    >
                      <span>{column.label}</span>
                      {sortField === (column.sortKey ?? column.key) && (
                        <span className="text-xs">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-sm">
            {loading ? (
              <tr>
                <td colSpan={HEADER_COLUMNS.length} className="px-4 py-10 text-center text-slate-500">
                  Loading overrides...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={HEADER_COLUMNS.length} className="px-4 py-10 text-center text-slate-500">
                  No contracts match the current filters.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const isNew = !!row.isNewSinceReview;
                return (
                <tr key={row.contractId} className={`hover:bg-slate-50/60 ${isNew ? 'bg-violet-50/70' : ''}`}>
                  <td className="px-4 py-3 align-top text-sm" style={{ width: columnWidthMap['title'] }}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-slate-900">{row.title}</span>
                      {isNew && (
                        <span className="inline-flex items-center rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">
                          New
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      {row.sourceUrl ? (
                        <a
                          href={row.sourceUrl}
                          target="_blank"
                          rel="nofollow noreferrer noopener"
                          aria-label={getOpportunitySourceAriaLabel(row.sourceType)}
                          className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                        >
                          {getOpportunitySourceName(row.sourceType)}
                        </a>
                      ) : (
                        <span>No link</span>
                      )}
                      <span>
                        First seen {row.firstSeen ? new Date(row.firstSeen).toLocaleDateString() : '—'}
                      </span>
                      <span>
                        Last seen {row.lastSeen ? new Date(row.lastSeen).toLocaleDateString() : '—'}
                      </span>
                    </div>
                    {row.description && (
                      <p className="mt-2 line-clamp-2 text-xs text-slate-600">{row.description}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top text-sm text-slate-700" style={{ width: columnWidthMap['city'] }}>
                    {row.procuringOrganization || row.city}
                  </td>
                  <td className="px-4 py-3 align-top" style={{ width: columnWidthMap['status'] }}>
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                      {row.status || 'Open'}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top text-sm text-slate-700" style={{ width: columnWidthMap['firstSeen'] }}>
                    {row.firstSeen ? new Date(row.firstSeen).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 align-top text-sm text-slate-700" style={{ width: columnWidthMap['dueDate'] }}>
                    {row.dueDate ? new Date(row.dueDate).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3 align-top text-sm text-slate-700" style={{ width: columnWidthMap['industryDisplay'] }}>
                    {row.industryDisplay || row.industryBase || '—'}
                  </td>
                  <td className="px-4 py-3 align-top text-sm text-slate-700" style={{ width: columnWidthMap['industryOverride'] }}>
                    <div className="flex items-center gap-2">
                      <select
                        className="w-48 rounded-lg border border-slate-200 px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        value={row.industryOverride ?? ''}
                        onChange={(event) =>
                          onOverrideChange(row.contractId, event.target.value ? event.target.value : null)
                        }
                      >
                        <option value="">Use base industry</option>
                        {taxonomy.map((value) => (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                      {row.industryOverride ? (
                        <button
                          type="button"
                          className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600 hover:bg-slate-200"
                          onClick={() => onOverrideChange(row.contractId, null)}
                        >
                          <XMarkIcon className="h-4 w-4" />
                          Clear
                        </button>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top text-sm text-slate-700" style={{ width: columnWidthMap['overrideNote'] }}>
                    <textarea
                      className="w-64 rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      placeholder="Add a note (optional)"
                      value={row.overrideNote || ''}
                      onChange={(event) => onNoteChange(row.contractId, event.target.value || null)}
                      rows={2}
                    />
                  </td>
                  <td className="px-4 py-3 align-top text-xs text-slate-500" style={{ width: columnWidthMap['overrideAt'] }}>
                    {row.overrideAt ? new Date(row.overrideAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 align-top text-xs text-slate-500" style={{ width: columnWidthMap['overrideBy'] }}>
                    {row.overrideByEmail || '—'}
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          Showing {rows.length} of {total} contracts
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm text-slate-600 disabled:opacity-40"
          >
            Previous
          </button>
          {showDropdown ? (
            <div className="flex items-center gap-2 rounded-full border border-slate-200 px-4 py-1.5 text-sm shadow-sm">
              <span className="text-slate-500">Page</span>
              <select
                className="bg-transparent text-slate-700 focus:outline-none"
                value={page}
                onChange={(event) => onPageChange(Number(event.target.value))}
              >
                {pageOptions.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
              <span className="text-slate-500">of {totalPages}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-full border border-slate-200 px-4 py-1.5 text-sm shadow-sm">
              <span className="text-slate-500">Page</span>
              <input
                type="number"
                min={1}
                max={totalPages}
                value={pageInput}
                onChange={(event) => setPageInput(event.target.value)}
                onBlur={handleManualPageChange}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleManualPageChange();
                  }
                }}
                className="w-20 border-none bg-transparent text-center text-slate-700 focus:outline-none"
              />
              <span className="text-slate-500">of {totalPages}</span>
            </div>
          )}
          <button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm text-slate-600 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default IndustryOverrideTable;
